import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Check, ChevronDown, Globe } from 'lucide-react'
import { motion } from "framer-motion"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { tokens } from "@/locales/translations/tokens"
import { TRANSLATION_LOCAL_STORAGE_KEY } from "@/locales/i18n"
import { updateUser } from "@/api/user"
import { toast } from "@/hooks/use-toast"

interface Language {
  code: string
  name: string
  nativeName: string
  flag: string
}

const languages: Language[] = [
  {
    code: "pt-BR",
    name: "Portuguese",
    nativeName: "Português",
    flag: "🇧🇷",
  },
  {
    code: "en-US",
    name: "English",
    nativeName: "English",
    flag: "🇺🇸",
  },
  // {
  //   code: "es-ES",
  //   name: "Spanish",
  //   nativeName: "Español",
  //   flag: "🇪🇸",
  // },
  // {
  //   code: "fr-FR",
  //   name: "French",
  //   nativeName: "Français",
  //   flag: "🇫🇷",
  // }
]

export default function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation()
  const [currentLanguage, setCurrentLanguage] = useState<Language | undefined>(undefined)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const savedTranslationLocale = localStorage.getItem(TRANSLATION_LOCAL_STORAGE_KEY);
    if(!savedTranslationLocale)
      localStorage.setItem(TRANSLATION_LOCAL_STORAGE_KEY, i18n.language);

    const translationTarget = localStorage.getItem("translation_locale") || i18n.language;
    const lang = languages.find((lang) => lang.code === translationTarget)
    setCurrentLanguage(lang || languages[0])
  }, [i18n.language])

  const handleChangeLanguage = async (lang: Language) => {
    try {
      i18n.changeLanguage(lang.code)
      localStorage.setItem(TRANSLATION_LOCAL_STORAGE_KEY, lang.code);
      setCurrentLanguage(lang)
      setIsOpen(false)

      await updateUser({ language: lang.code })
    } catch (_error) {
      toast({
        variant: "destructive",
        title: t(tokens.ui.toasts.error.title),
        description: t(tokens.ui.toasts.error.generic)
      })
    }
  }

  if (!currentLanguage) return null

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "flex items-center gap-2 px-2 h-9 rounded-full hover:bg-muted/80 transition-colors",
            className
          )}
        >
          <span className="text-lg">{currentLanguage.flag}</span>
          <span className="text-sm font-medium hidden sm:inline-block">
            {currentLanguage.nativeName}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px] p-2">
        <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
          <Globe className="h-4 w-4" />
          <span>{t(tokens.ui.selectlanguage)}</span>
        </div>
        <div className="h-px bg-muted my-1" />
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            className={cn(
              "flex items-center gap-2 cursor-pointer rounded-md px-2 py-1.5",
              currentLanguage.code === lang.code && "bg-muted"
            )}
            onClick={() => handleChangeLanguage(lang)}
          >
            <div className="flex items-center gap-2 flex-1">
              <span className="text-lg">{lang.flag}</span>
              <span className="text-sm">{lang.nativeName}</span>
            </div>
            {currentLanguage.code === lang.code && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.2 }}
              >
                <Check className="h-4 w-4 text-primary" />
              </motion.div>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
